import { QueryLazyOptions } from "@apollo/client";
import { Button } from "@material-ui/core";
import MyTypography from "@saleor/components/MyTypography";
import { ContentHeaderTab } from "@saleor/createOrChangeNotRefContract/views/createOrChangeNotRefContractView/const";
import { Exact } from "@saleor/graphql";
import { makeStyles } from "@saleor/macaw-ui";
import { renderCollection } from "@saleor/misc";
import classNames from "classnames";
import { FormikProps } from "formik";
import React from "react";

import AdditionalTab from "./Tab/AdditionalTab";
import NotRefSalesPartnerForm from "./Tab/NotRefSalesPartner";

interface HeaderTabProps {
  activeTab: ContentHeaderTab;
  setActiveTab: (value) => void;
  formik: FormikProps<any>;
  showDetails: boolean;
  handleShowItemDetailsTab: any;
  handleSalesOrg: any;
  handleSoldTo: any;
  fetchOrderData: (
    options?: QueryLazyOptions<
      Exact<{
        orderId: string;
      }>
    >
  ) => void;
  shouldRecheckBlock?: boolean;
  orderId: string;
  formikOneTimeCust?: FormikProps<any>;
  onApplyAll: () => void;
  isChangeFlow?: boolean;
  isViewMode?: boolean;
  formikItemDetails: FormikProps<any>;
  saleOrgLoading?: boolean;
  salesDataloadingCompleted?: boolean;
}

const useStyles = makeStyles(
  theme => ({
    root: {
      marginTop: "4rem"
    },
    tableTabActive: {
      WebkitClipPath: "polygon(0 0, 78% 0, 100% 100%, 0% 100%)",
      clipPath: "polygon(0 0, 78% 0, 100% 100%, 0% 100%)",
      width: "200px",
      height: "40px",
      boxShadow: "2px 0px 4px rgba(0, 0, 0, 0.25)",
      borderTopLeftRadius: "6px",
      background: theme.palette.info.dark,
      display: "flex",
      alignItems: "center",
      paddingLeft: "2.4rem",
      cursor: "pointer",
      position: "relative",
      borderBottom: 0
    },
    innerTabActive: {
      WebkitClipPath: "polygon(0 0, 77.5% 0, 99% 100%, 0% 100%)",
      clipPath: "polygon(0 0, 77.5% 0, 99% 100%, 0% 100%)",
      width: "199px",
      height: "39px",
      boxShadow: "2px 0px 4px rgba(0, 0, 0, 0.25)",
      borderTopLeftRadius: "5px",
      display: "flex",
      alignItems: "center",
      paddingLeft: "2.4rem",
      cursor: "pointer",
      top: 1,
      left: 1,
      background: theme.palette.info.main,
      position: "absolute",
      borderBottom: 0,
      color: "#FFFFFF"
    },
    tableTab: {
      WebkitClipPath: "polygon(0 0, 78% 0, 100% 100%, 0% 100%)",
      clipPath: "polygon(0 0, 78% 0, 100% 100%, 0% 100%)",
      width: "200px",
      height: "40px",
      boxShadow: "2px 0px 4px rgba(0, 0, 0, 0.25)",
      borderTopLeftRadius: "6px",
      background: theme.palette.grey[400],
      display: "flex",
      alignItems: "center",
      paddingLeft: "2.4rem",
      cursor: "pointer",
      position: "relative",
      borderBottom: 0
    },
    innerTab: {
      WebkitClipPath: "polygon(0 0, 77.5% 0, 99% 100%, 0% 100%)",
      clipPath: "polygon(0 0, 77.5% 0, 99% 100%, 0% 100%)",
      width: "199px",
      height: "39px",
      boxShadow: "2px 0px 4px rgba(0, 0, 0, 0.25)",
      borderTopLeftRadius: "5px",
      display: "flex",
      alignItems: "center",
      paddingLeft: "2.4rem",
      cursor: "pointer",
      top: 1,
      left: 1,
      background: theme.palette.grey[300],
      color: theme.palette.grey[600],
      position: "absolute",
      borderBottom: 0
    },
    tabB: {
      marginLeft: "-3rem"
    },
    z1: {
      zIndex: 0
    },
    z2: {
      zIndex: 1
    },
    z3: {
      zIndex: 3
    },
    zMax: {
      zIndex: 100
    },
    tabBox: {
      display: "flex"
    },
    boxContentTab: {
      background: "#FFF",
      border: `solid 1px ${theme.palette.info.dark}`,
      padding: "3.7rem 2.4rem"
    },
    brdRight: {
      borderRight: "1px solid #009FB8"
    }
  }),
  { name: "HeaderTabCS" }
);

const tabList = [
  {
    id: "sales-and-partner-tab",
    code: ContentHeaderTab.salesPartner,
    title: "Sales & Partner",
    index: "z2",
    first: true,
    showTab: true
  },
  {
    id: "additional-tab",
    code: ContentHeaderTab.additionalData,
    title: "Additional Data",
    index: "z1",
    first: false,
    showTab: true
  }
];

const HeaderTab: React.FC<HeaderTabProps> = props => {
  const {
    formik,
    activeTab,
    setActiveTab,
    showDetails,
    handleShowItemDetailsTab,
    handleSalesOrg,
    handleSoldTo,
    fetchOrderData,
    shouldRecheckBlock,
    orderId,
    formikOneTimeCust,
    onApplyAll,
    isChangeFlow,
    isViewMode,
    formikItemDetails,
    saleOrgLoading,
    salesDataloadingCompleted
  } = props;

  const classes = useStyles();
  const onTabClick = tab => {
    setActiveTab(tab);
  };
  tabList[1].showTab = showDetails;

  const renderAdditionalTab = (
    <AdditionalTab formik={formik} isViewMode={isViewMode} />
  );

  const renderNotRefSalesPartnerForm = (
    <NotRefSalesPartnerForm
      formik={formik}
      showDetails={showDetails}
      handleShowDetails={handleShowItemDetailsTab}
      handleSalesOrg={handleSalesOrg}
      handleSoldTo={handleSoldTo}
      fetchOrderData={fetchOrderData}
      shouldRecheckBlock={shouldRecheckBlock}
      orderId={orderId}
      formikOneTimeCust={formikOneTimeCust}
      onApplyAll={onApplyAll}
      isChangeFlow={isChangeFlow}
      isViewMode={isViewMode}
      formikItemDetails={formikItemDetails}
      saleOrgLoading={saleOrgLoading}
      salesDataloadingCompleted={salesDataloadingCompleted}
    />
  );

  return (
    <div className={classes.root}>
      <div className={classes.tabBox}>
        {renderCollection(
          tabList,
          tab =>
            tab.showTab && (
              <div
                key={tab.code}
                className={classNames(
                  activeTab === tab.code ? classes.zMax : classes[tab.index]
                )}
              >
                <Button
                  id={tab.id}
                  data-testid={tab.id}
                  onClick={() => onTabClick(tab.code)}
                  className={classNames(
                    !tab.first && classes.tabB,
                    activeTab === tab.code
                      ? classes.tableTabActive
                      : classes.tableTab
                  )}
                >
                  <MyTypography
                    variant={"body2"}
                    className={classNames(
                      activeTab === tab.code
                        ? classes.innerTabActive
                        : classes.innerTab
                    )}
                  >
                    {tab.title}
                  </MyTypography>
                </Button>
              </div>
            )
        )}
      </div>
      <div className={classes.boxContentTab}>
        <div hidden={activeTab !== ContentHeaderTab.salesPartner}>
          {renderNotRefSalesPartnerForm}
        </div>
        <div hidden={activeTab !== ContentHeaderTab.additionalData}>
          {renderAdditionalTab}
        </div>
      </div>
    </div>
  );
};

export default HeaderTab;
